import * as t from 'io-ts';

import type { Filter } from 'header/types';
import { concatUniqByProp } from 'portlet/common/utils';

import type { TypedFilterValue } from './filterValue';
import { FilterValue, FilterValueInputC } from './filterValue';

const FilterIdC = t.string;
export type FilterId = t.TypeOf<typeof FilterIdC>;

const IsInteractionEnabledC = t.boolean;
export type isInteractionEnabled = t.TypeOf<typeof IsInteractionEnabledC>;

export const ListenFiltersC = t.type({
    filterId: FilterIdC,
    isInteractionEnabled: IsInteractionEnabledC,
});
export type ListenFilter = t.TypeOf<typeof ListenFiltersC>;

const FilterDataC = t.type({
    item: FilterIdC,
    value: FilterValue.codec,
});

export type FilterData = t.TypeOf<typeof FilterDataC>;
export type TypedFilterData<K extends FilterValue['type']> = FilterData & {
    value: TypedFilterValue<K>;
};

const FilterDataInputC = t.type({
    item: FilterIdC,
    value: t.union([FilterValue.codec, FilterValueInputC]),
});
export type FilterDataInput = t.TypeOf<typeof FilterDataInputC>;
export const FilterDataInput = { codec: FilterDataInputC };

export type ClearFilterData = Pick<FilterData, 'item'>;

export const FilterData = {
    codec: FilterDataC,

    toFilterDefinition: ({ item, value }: FilterData): Filter => ({
        filterKey: item,
        filterValue: value,
        filterKeyName: item,
        filterApplied: [item],
        filterKeyApplied: [item],
    }),

    is: {
        DIMENSION: (filter: FilterData): filter is TypedFilterData<'DIMENSION'> =>
            FilterValue.is.DIMENSION(filter.value),
        DATE: (filter: FilterData): filter is TypedFilterData<'DATE'> =>
            FilterValue.is.DATE(filter.value),
        DATE_RANGE: (filter: FilterData): filter is TypedFilterData<'DATE_RANGE'> =>
            FilterValue.is.DATE_RANGE(filter.value),
        HIERARCHY: (filter: FilterData): filter is TypedFilterData<'HIERARCHY'> =>
            FilterValue.is.HIERARCHY(filter.value),
        SCALE: (filter: FilterData): filter is TypedFilterData<'SCALE'> =>
            FilterValue.is.SCALE(filter.value),
        SMART_VIEW_TAG: (filter: FilterData): filter is TypedFilterData<'SMART_VIEW_TAG'> =>
            FilterValue.is.SMART_VIEW_TAG(filter.value),
        ROLE: (filter: FilterData): filter is TypedFilterData<'ROLE'> =>
            FilterValue.is.ROLE(filter.value),
    },
};

/**
 * Represents list of filters, which is order sensitive - most recent filters are placed first
 */
export type FilterDataArray = FilterData[];

export const FilterDataArray = {
    codec: t.array(FilterData.codec),

    updateFilter: (currentFilters: FilterDataArray, updatedFilter: FilterData): FilterDataArray => {
        return [updatedFilter, ...currentFilters.filter(({ item }) => item !== updatedFilter.item)];
    },

    toFilterDefinition: ({ item, value }: FilterData): Filter => ({
        filterKey: item,
        filterValue: value,
        filterKeyName: item,
        filterApplied: [item],
        filterKeyApplied: [item],
    }),

    clearFilter: (
        currentFilters: FilterDataArray,
        { item: filterItem }: ClearFilterData,
    ): FilterDataArray => currentFilters.filter(({ item }) => item !== filterItem),

    clearFilters: (currentFilters: FilterDataArray, items: ClearFilterData[]): FilterDataArray => {
        const itemIds = items.map(({ item }) => item);

        return currentFilters.filter(({ item }) => !itemIds.includes(item));
    },

    /** elements of first array have higher priority */
    merge: concatUniqByProp('item'),

    toReplacementTags: (filters: FilterDataArray): Record<string, string> => {
        const format = (f: FilterData) =>
            FilterValue.match(f.value, {
                DIMENSION: ({ value }) => ({ [f.item]: value.map(({ value }) => value).join(',') }),
                DATE: ({ value, dateType }) => ({ [f.item]: formatSelectorDate(dateType, value) }),
                DATE_RANGE: ({ value: [from, to], dateType }) => ({
                    [f.item]: `${formatSelectorDate(dateType, from)}-${formatSelectorDate(
                        dateType,
                        to,
                    )}`,
                }),
                // for scale with value 1 (no scaling) we want to display empty string instead of `None`
                SCALE: ({ value }) => ({ [f.item]: value.key === '1' ? '' : value.label }),
                HIERARCHY: ({ value }) => {
                    const initialTitle = 'All';
                    const allLevels = {
                        [f.item]: value.values.map(({ value }) => value).join(','),
                    };

                    const initialObject: Record<string, string> =
                        value.hierarchyInternalKeys.reduce(
                            (acc, internalKey) => ({
                                ...acc,
                                [`${f.item}.${internalKey}`]: initialTitle,
                            }),
                            {},
                        );

                    const specificLevels = value.values.reduce((acc, value) => {
                        const key = `${value.hierarchyId}.${value.internalKey}`;
                        const property =
                            acc[key] != initialTitle ? `${acc[key]}, ${value.value}` : value.value;
                        return Object.assign(acc, {
                            [key]: property,
                        });
                    }, initialObject);

                    return Object.assign(allLevels, specificLevels);
                },
                SMART_VIEW_TAG: ({ viewTag }) => ({ [f.item]: viewTag }),
                ROLE: ({ value }) => ({ [f.item]: value.label }),
                NUMBER: ({ value }) => ({ [f.item]: value.toLocaleString() }),
            });

        return filters.map(filter => format(filter)).reduce((r, c) => Object.assign(r, c), {});
    },
};
//https://stackoverflow.com/a/6117889
function formatWeek(inDate: Date): string {
    // Copy date so don't modify original
    const date = new Date(Date.UTC(inDate.getFullYear(), inDate.getMonth(), inDate.getDate()));
    // Set to nearest Thursday: current date + 4 - current day number
    // Make Sunday's day number 7
    date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
    // Get first day of year
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    // Calculate full weeks to nearest Thursday
    const weekNo = Math.ceil(((date.valueOf() - yearStart.valueOf()) / 86400000 + 1) / 7);
    return `Week ${weekNo}, ${date.getUTCFullYear()}`;
}

function formatSelectorDate(
    dateType: TypedFilterValue<'DATE'>['dateType'] = 'date',
    dateValue: string,
) {
    // iso timestamp stored in filter represents the date as in utc timezone
    // when parsing it back to Date object in order to format it,
    // we need to adjust it so that it represents the same displayed value (not the same unix time) in local timezone
    const utcDateInLocal = new Date(dateValue);
    const localDate = new Date(
        utcDateInLocal.getTime() + utcDateInLocal.getTimezoneOffset() * 60 * 1000,
    );

    switch (dateType) {
        case 'month':
            return localDate.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
            });
        case 'year':
            return String(localDate.getFullYear());
        case 'week':
            return formatWeek(localDate);
        default:
            return localDate.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: '2-digit',
            });
    }
}
